import { Box, CircularProgress, IconButton, Paper, Typography, useMediaQuery } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useParams, Navigate, useNavigate } from "react-router";
import { useQuery } from "@apollo/client";
import CloseIcon from "@mui/icons-material/Close";
import DashboardHeader from "../../components/dashboard-header";
import { useUserContext } from "../../hooks/userContext";
import { useGetDesignerMatchingProfileQuery } from "../../hooks/use-get-designer-matching-profile";
import { GET_HOMEOWNER_MATCHING_PROFILE } from "../../graphql/homeowner-matching-profile";
import { Pages } from "../../utils";
import PersonalityTab from "../homeowner-profile/tabs/PersonalityTab";
import { CARD_BORDER, CARD_RADIUS } from "../homeowner-profile/constants";
import { formatPersonalityType, formatValue } from "../homeowner-profile/utils";
import { fetchMatchProfile, fetchMatches, type MatchProfileDetail } from "../../services/matchesApi";
import { getMatchCandidateIds } from "../matches-page/utils";
import "./styles.scss";

export const DesignerCompatibilityDetailsPage = () => {
  const { designerId } = useParams<{ designerId: string }>();
  const { user, loading } = useUserContext();
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width: 900px)");
  const [designerProfile, setDesignerProfile] = useState<any>(null);
  const [designerError, setDesignerError] = useState<string | null>(null);
  const [matchProfile, setMatchProfile] = useState<MatchProfileDetail | null>(null);
  const [matchScore, setMatchScore] = useState<number | null>(null);
  const [matchLoading, setMatchLoading] = useState(false);
  const [matchScoreLoading, setMatchScoreLoading] = useState(false);

  const { getDesignerMatchingProfile, loading: designerLoading } =
    useGetDesignerMatchingProfileQuery();

  const { data: homeownerData, loading: homeownerLoading } = useQuery(
    GET_HOMEOWNER_MATCHING_PROFILE,
    {
      variables: { homeOwnerId: user?.id ?? "" },
      skip: !user?.id,
    }
  );

  useEffect(() => {
    if (isMobile) {
      document.body.classList.add("designer-compatibility-mobile");
    } else {
      document.body.classList.remove("designer-compatibility-mobile");
    }
    return () => {
      document.body.classList.remove("designer-compatibility-mobile");
    };
  }, [isMobile]);

  useEffect(() => {
    if (!designerId) return;
    setDesignerError(null);
    getDesignerMatchingProfile(designerId, true)
      .then((result) => {
        setDesignerProfile(result ?? null);
      })
      .catch(() => {
        setDesignerError("Unable to load designer compatibility details.");
      });
  }, [designerId, getDesignerMatchingProfile]);

  useEffect(() => {
    if (!designerId) return;
    let cancelled = false;
    setMatchLoading(true);
    fetchMatchProfile(designerId)
      .then((result) => {
        if (!cancelled) setMatchProfile(result ?? null);
      })
      .catch(() => {
        if (!cancelled) setMatchProfile(null);
      })
      .finally(() => {
        if (!cancelled) setMatchLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [designerId]);

  useEffect(() => {
    if (!designerId || !user?.id) {
      setMatchScore(null);
      setMatchScoreLoading(false);
      return;
    }
    let cancelled = false;
    setMatchScoreLoading(true);
    const loadMatchScore = async () => {
      let page = 1;
      let foundScore: number | null = null;
      let hasNextPage = true;
      while (hasNextPage && foundScore === null && page <= 6) {
        const data = await fetchMatches(user.id, page);
        const match = data.matches.find((candidate) =>
          getMatchCandidateIds(candidate as any).includes(designerId)
        );
        const score =
          match && typeof match.matchScore === "number" && !Number.isNaN(match.matchScore)
            ? match.matchScore
            : null;
        if (score !== null) {
          foundScore = score;
          break;
        }
        hasNextPage = Boolean(data.hasNextPage);
        page += 1;
      }
      if (!cancelled) {
        setMatchScore(foundScore);
      }
    };
    loadMatchScore()
      .catch(() => {
        if (!cancelled) {
          setMatchScore(null);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setMatchScoreLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [designerId, user?.id]);

  const homeownerProfile = homeownerData?.getHomeOwnerMatchingProfile ?? null;
  const homeownerPersonalityType = useMemo(
    () =>
      formatValue(
        formatPersonalityType(homeownerProfile?.personalityType),
        "Extrovert Blue Orange"
      ),
    [homeownerProfile?.personalityType]
  );
  const designerPersonalityType =
    designerProfile?.personalityType ??
    matchProfile?.personalityStyleResult ??
    null;
  const resolvedMatchScore = useMemo(() => {
    if (typeof matchScore === "number" && !Number.isNaN(matchScore)) {
      return matchScore;
    }
    if (typeof matchProfile?.compatibilityRating === "number") {
      return Math.max(0, Math.min(100, matchProfile.compatibilityRating * 20));
    }
    return null;
  }, [matchProfile?.compatibilityRating, matchScore]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress sx={{ color: "#00A06D" }} />
      </Box>
    );
  }

  if (!user?.id) {
    return <Navigate to={Pages.LOGIN} replace />;
  }

  if (!designerId) {
    return <Navigate to={Pages.DASHBOARD} replace />;
  }

  if (homeownerLoading || designerLoading || matchLoading || matchScoreLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress sx={{ color: "#00A06D" }} />
      </Box>
    );
  }

  if (designerError) {
    return (
      <Box sx={{ px: { xs: 2, md: 3 }, py: 6 }}>
        <Typography color="error">{designerError}</Typography>
      </Box>
    );
  }

  return (
      <Box>
      {isMobile ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2,
            py: 1.5,
          }}
        >
          <Typography sx={{ fontWeight: 600 }}>Compatibility Details</Typography>
          <IconButton aria-label="Close compatibility details" onClick={() => navigate(-1)}>
            <CloseIcon />
          </IconButton>
        </Box>
      ) : (
        <DashboardHeader title="Compatibility Details" showBackButton={true} />
      )}
      <Box sx={{ width: "100%", px: { xs: 0, md: 3 }, pb: { xs: 0, md: 6 } }}>
        <Paper
          elevation={0}
          sx={{
            borderRadius: { xs: 0, md: CARD_RADIUS },
            border: { xs: "none", md: CARD_BORDER },
            backgroundColor: "#fff",
            overflow: "visible",
            position: "relative",
            px: { xs: 2, md: 3 },
            py: { xs: 0, md: 3 },
          }}
        >
          <PersonalityTab
            personalityType={homeownerPersonalityType}
            profilePersonalityType={homeownerProfile?.personalityType}
            designerPersonalityType={designerPersonalityType}
            viewerRole="homeowner"
            displayMode="compatibility"
            matchScore={resolvedMatchScore}
            showHeaderCompatibilityRating={false}
          />
        </Paper>
      </Box>
    </Box>
  );
};

export default DesignerCompatibilityDetailsPage;
